import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ShieldCheck, Award, Leaf, Clock, ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/918882183014?text=Hi%2C%20I%20want%20to%20book%20overhead%20water%20tank%20cleaning%20in%20Gurugram";

const faqs = [
  { q: "How often should I clean my overhead water tank?", a: "We recommend cleaning your overhead water tank every 6 months. In areas with high dust or pollution like Gurugram, quarterly cleaning ensures your family drinks safe, bacteria-free water year-round." },
  { q: "How long does overhead tank cleaning take?", a: "A standard overhead tank (500-2000 litres) takes about 2-3 hours. This includes inspection, draining, scrubbing, high-pressure jet cleaning, and anti-bacterial disinfection." },
  { q: "Is your cleaning process safe for drinking water?", a: "Absolutely. We use only food-grade, WHO-approved cleaning agents that are 100% safe for drinking water. After cleaning, your tank is immediately ready for water storage." },
  { q: "Do you provide same-day service in Gurugram?", a: "Yes! TankBuddy offers same-day overhead tank cleaning across all Gurugram sectors. Book before 12 PM and we'll arrive the same day, subject to availability." },
  { q: "What is the cost of overhead tank cleaning in Gurugram?", a: "Our overhead tank cleaning starts at just ₹999 for tanks up to 1000 litres. Pricing varies based on tank size, condition, and accessibility. Contact us for an exact quote." },
];

const steps = [
  { title: "Inspection", desc: "Thorough assessment of your overhead tank's condition, water quality, and sludge buildup." },
  { title: "Dewatering", desc: "Safe draining of existing water and preliminary removal of sediment and debris." },
  { title: "High Pressure Jet Cleaning", desc: "Industrial-grade jet washing at 150+ bar pressure to remove algae, biofilm, and mineral deposits from walls and floor." },
  { title: "Disinfection", desc: "Anti-bacterial UV treatment and food-grade sanitizer application ensuring 99.99% germ elimination." },
];

function FAQ({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors">
        <span className="font-medium text-navy text-sm pr-4">{faq.q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-sky flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-sky flex-shrink-0" />}
      </button>
      {open && <div className="px-4 pb-4 text-slate-600 text-sm leading-relaxed">{faq.a}</div>}
    </div>
  );
}

export default function OverheadTankCleaning() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Overhead Water Tank Cleaning in Gurugram | TankBuddy</title>
        <meta name="description" content="Professional overhead water tank cleaning in Gurugram. Starting ₹999. Same-day service, certified technicians, eco-friendly process. Book TankBuddy now!" />
        <meta name="keywords" content="overhead tank cleaning Gurugram, rooftop tank cleaning Gurgaon, water tank cleaning service" />
        <link rel="canonical" href="https://www.tankbuddy.in/overhead-water-tank-cleaning-gurugram" />
        <meta property="og:title" content="Overhead Water Tank Cleaning in Gurugram | TankBuddy" />
        <meta property="og:description" content="Professional overhead water tank cleaning in Gurugram. Starting ₹999. Same-day service." />
        <meta property="og:url" content="https://www.tankbuddy.in/overhead-water-tank-cleaning-gurugram" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Overhead Water Tank Cleaning in Gurugram",
            "provider": {
              "@type": "LocalBusiness",
              "name": "TankBuddy",
              "telephone": "+91-8882183014",
              "url": "https://www.tankbuddy.in",
              "address": {"@type": "PostalAddress", "addressLocality": "Gurugram", "addressRegion": "Haryana", "addressCountry": "IN"}
            },
            "areaServed": "Gurugram",
            "description": "Professional overhead water tank cleaning service in Gurugram with high-pressure jet cleaning and anti-bacterial disinfection.",
            "offers": {"@type": "Offer", "price": "999", "priceCurrency": "INR", "priceValidUntil": "2026-12-31"}
          }
        `}</script>
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">
          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            Professional Overhead Water Tank Cleaning in Gurugram
          </h1>

          <div className="flex flex-wrap gap-4 mb-10">
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><ShieldCheck className="w-4 h-4" /> Certified Technicians</span>
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><Leaf className="w-4 h-4" /> Eco-friendly</span>
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><Clock className="w-4 h-4" /> Same-day Service</span>
          </div>

          <div className="prose prose-slate max-w-none mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">What is Overhead Water Tank Cleaning?</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Overhead water tank cleaning is the professional process of removing accumulated dirt, sludge, algae, bacteria, and other contaminants from rooftop and elevated water storage tanks. Over time, even well-sealed tanks develop sediment buildup, biofilm layers, and mineral deposits that compromise water quality and pose serious health risks to your family.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              In Gurugram's climate, with high temperatures and dust levels, overhead tanks are particularly susceptible to rapid bacterial growth and contamination. TankBuddy uses industrial-grade high-pressure jet cleaning equipment operating at 150+ bar pressure combined with WHO-approved anti-bacterial agents to restore your tank to pristine condition. Our certified technicians ensure every corner, wall, and surface is thoroughly cleaned and disinfected, providing you with 100% bacteria-free water storage.
            </p>

            <h2 className="font-heading font-bold text-2xl text-navy mb-4 mt-10">Why Overhead Tank Cleaning is Important</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Neglected overhead tanks become breeding grounds for harmful bacteria like E. coli, Salmonella, and Legionella. Algae growth, mosquito larvae, and rust particles accumulate silently, turning your water supply into a health hazard. Studies show that uncleaned tanks can harbor 10x more bacteria than permissible limits within just 6 months.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Regular professional cleaning prevents waterborne diseases, eliminates foul odors and discoloration, extends your tank's lifespan, and ensures compliance with municipal water safety guidelines. For families in Gurugram, where water quality already varies by sector, overhead tank hygiene is non-negotiable for safe drinking water.
            </p>
          </div>

          <div className="bg-surface border border-slate-200 rounded-xl p-8 mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Our 4-Step Cleaning Process</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {steps.map((s, i) => (
                <div key={s.title} className="flex gap-4">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-sky font-bold text-sm">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-navy mb-1">{s.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-12 text-white">
            <h2 className="font-heading font-bold text-2xl mb-2">Pricing</h2>
            <p className="text-slate-300 mb-4">Overhead tank cleaning in Gurugram</p>
            <div className="text-4xl font-heading font-extrabold mb-1">Starting ₹999</div>
            <p className="text-slate-400 text-sm mb-6">For tanks up to 1,000 litres. Larger tanks quoted on inspection.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp</a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+918882183014"><Phone className="w-4 h-4 mr-2" /> Call +91 88821 83014</a>
              </Button>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => <FAQ key={i} faq={faq} />)}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
